/**
 * The wire shape of `/auth/context/*` — "where may I act?".
 *
 * Mirrors `API.Core/Authorization/ContextSelection.cs` + `API.Core/Models/Authentication/ContextSessionModels.cs`.
 *
 * **The two enums are NUMBERS on the wire, not strings.** The monolith serialises with Newtonsoft
 * (`API/Program.cs` → `AddNewtonsoftJson`) and registers no `StringEnumConverter`, so a C# enum crosses as its
 * ordinal. Declaring these as string enums would compile, render "undefined" everywhere and post a `View` the
 * backend reads as `Platform` — so the ordinals here are load-bearing and must stay in the C# declaration order.
 *
 * `NullValueHandling.Ignore` is also set, so every nullable field (`companyId`, `shardId`, `shardName`,
 * `autoSelect`) is **omitted** rather than sent as null — hence the `?` on each of them.
 */

/** What kind of thing is being entered. Ordinals mirror `SelectableContextKind`. */
export enum SelectableContextKind {
  /** The platform shell — platform management only, the one legitimate authenticated state with no company. */
  Platform = 0,
  /** A whole company, no shard (e.g. a payroll or company-wide auditor session). */
  Company = 1,
  /** One shard of a company. */
  Shard = 2,
  /**
   * One **employee** of a company that this account brackets (an `ExternalIdentityEmployee` link).
   *
   * The only kind that does not come from a role grant, and it cannot: an employee's entitlement is the
   * employment itself (ADR #260 §4), so there is no grant row to derive one from and never will be.
   * Company-scoped with no shard, because an `Employee` row is company-scoped and its token carries a company
   * claim and no shard.
   */
  Employee = 3
}

/**
 * Which application entering a context opens. Ordinals mirror `ContextView`.
 *
 * Part of a context's **identity**, not decoration: somebody who is both an employee and an operator at one
 * shard has two genuinely different places to be, so the switcher must offer both rather than collapsing them.
 */
export enum ContextView {
  Platform = 0,
  Management = 1,
  Employee = 2,
  Operator = 3
}

/** One entry in the selector, with the names needed to render it. */
export interface SelectableContext {
  kind: SelectableContextKind;
  view: ContextView;
  /** Absent only for the platform shell. */
  companyId?: string;
  companyName?: string;
  /** Present only for a shard context. */
  shardId?: string;
  shardName?: string;
  /**
   * What this context would confer — already narrowed to the view, i.e. the roles the token *would* carry. This
   * is what lets the selector say what you are about to become instead of just where you are going.
   */
  roles: string[];
  /**
   * Present only for {@link SelectableContextKind.Employee}, and posted **back** on entry: it is part of the
   * entry's identity, because one account can bracket two employees of the same company.
   */
  employeeId?: string;
  /**
   * Which person this entry signs you in as. The only thing that tells two employee entries of one company
   * apart on screen — and it comes from the display name cached on the link row, so building the list never
   * touches a company database.
   */
  employeeName?: string;
}

/** The answer to "where may I act?". */
export interface ContextOptions {
  contexts: SelectableContext[];
  /**
   * The single context to enter without asking, or absent when there is a real choice. The backend sets it only
   * when there is exactly one option: the common case in this product is one person at one shard, and making
   * them dismiss a selector to pick their only option is a daily irritation for no safety gain.
   */
  autoSelect?: SelectableContext;
  /**
   * True for a platform owner, whose list is "every tenant that exists" — `contexts` is then NOT their full
   * list. See {@link canEnter}: there is no search endpoint today, and entering a tenant they hold no grant in
   * is refused by the backend, so the UI says so rather than pretending to enumerate.
   */
  requiresSearch: boolean;
  /**
   * Companies this account holds live grants in that opened **nothing** (#279, Defect A). Absent or empty in
   * every ordinary case, and omitted entirely by a backend that predates it — so read it defensively.
   */
  dormant?: DormantContext[];
}

/**
 * Why a company this account holds live grants in currently opens **no** context. Ordinals mirror
 * `DormantContextReason` in `API.Core/Authorization/ContextSelection.cs`.
 */
export enum DormantContextReason {
  /**
   * Every grant held for the company is one that `RoleGrantExpander.CascadeToEveryShard` expresses *through* a
   * location, and the company has none. This is the shape a new customer's owner is invited into — invite the
   * owner, then set the business up — which is why it is the reason that actually occurs.
   */
  NoShards = 0,
  /** The grants name no destination view at all. Nothing produces this today. */
  NoDestination = 1
}

/**
 * One company that was granted to this account and nonetheless opens nothing.
 *
 * **The difference between "you hold nothing" and "you hold something that does not open yet".** Without it the
 * chooser tells somebody holding a live `CompanyOwner` grant that no access has been given to their account —
 * which is false, and sends them off to ask for an invitation they already redeemed.
 */
export interface DormantContext {
  companyId: string;
  /** Absent when the main DB could not name the company; the id is then the only thing there is to show. */
  companyName?: string;
  reason: DormantContextReason;
}

/**
 * The German sentences for a company whose grants open nothing — a headline and the instruction under it.
 *
 * Two strings rather than one paragraph because the screen renders them as a heading and a body, and a heading
 * that is a whole sentence wraps to three lines on a phone.
 *
 * **Written for the person who cannot fix it.** The invitee is not a platform owner, so "create a location" is
 * not an instruction they can follow — `ShardController` is owner-only. The instruction is therefore to go back
 * to whoever invited them, and the sentence says *what* has to happen so that the request they make is the
 * right one. ASD-STE100: one instruction per sentence, active voice, present tense, formal `Sie`.
 */
export function dormantContextMessage(dormant: DormantContext): {title: string; body: string} {
  const company = dormant.companyName || dormant.companyId;

  switch (dormant.reason) {
    case DormantContextReason.NoShards:
      return {
        title: `„${company}“ hat noch keinen Standort`,
        body: 'Ihr Zugang für diese Firma gilt für alle Standorte. Es gibt aber noch keinen. '
          + 'Bitten Sie die Person, die Sie eingeladen hat, einen Standort anzulegen. '
          + 'Danach melden Sie sich erneut an.'
      };
    default:
      // A reason this build has no wording for. Naming the company and pointing at the inviter is still true
      // and still actionable; inventing a cause would not be.
      return {
        title: `Ihr Zugang für „${company}“ ist noch nicht nutzbar`,
        body: 'Die Firma ist noch nicht vollständig eingerichtet. '
          + 'Bitten Sie die Person, die Sie eingeladen hat, die Einrichtung abzuschließen.'
      };
  }
}

/**
 * Notice codes {@link ChooseContextComponent} itself knows how to render, carried on its own URL as
 * `?notice=<code>` — minted entirely CLIENT-side, never by the backend.
 *
 * Distinct from `SocialLoginNotice` (`src/app/enums/social-login-notice.ts`), which mirrors backend
 * `AuthErrorCodes` on the external sign-in handoff redirect: that notice exists because the SERVER decided
 * something worth explaining happened during a redirect it controls. This one exists because `ErrorInterceptor`
 * reacts to a 401 by silently recovering the platform/base session and bouncing back here on its OWN
 * initiative — there is no backend redirect in that path to carry a code on, so the interceptor mints one
 * itself and this screen reads it back.
 */
export const ContextRecoveryNotice = {
  /**
   * A time-boxed platform-owner self-grant expired, the backend sweep purged the company-scoped session
   * underneath it, and `ErrorInterceptor` recovered the platform/base session rather than logging out. See
   * `AuthenticationService.recoverBaseSession`.
   */
  AccessExpired: 'access-expired'
} as const;

/** The body `POST /auth/context/enter` takes. The secret rides the CM-Secret cookie, never this payload. */
export interface EnterContextRequest {
  CompanyId: string;
  ShardId: string | null;
  View: ContextView;
  /**
   * Which bracketed employee to enter as; null for every other kind.
   *
   * Not an authorization input — the backend recomputes the enterable set and refuses anything outside it — but
   * it *is* part of the entry's identity. One account can bracket two employees of the same company, and
   * without this the backend's match would resolve to whichever it happened to list first: a session minted as
   * the wrong person, which is a different failure from a refused one.
   */
  EmployeeId: string | null;
}

/**
 * Whether this context can actually be entered through `POST /auth/context/enter`.
 *
 * **The platform shell can.** It is the one destination with no tenant, requested as `View = Platform` with a
 * blank `CompanyId`/`ShardId`, and `ContextSessionService.EnterPlatform` authorizes it against the identity
 * re-read from the database — the session asking has already been narrowed away from `PlatformOwner`, so the
 * token could not answer the question even for somebody entitled to it. Every *other* view still needs a
 * company id, and asking without one is still a 400.
 *
 * Note this says nothing about whether a given *surface* should offer it: right after sign-in a platform
 * principal already holds that session, so {@link ChooseContextComponent} deliberately does not — see its
 * `selectable()`.
 */
export function canEnter(context: SelectableContext): boolean {
  if (context.kind === SelectableContextKind.Platform) return !context.companyId;
  return !!context.companyId;
}

/**
 * Whether a **freshly signed-in** session should POST an entry for this context, rather than just landing.
 *
 * The difference from {@link canEnter} is one context and one moment: a platform principal arrives from the
 * handoff already *holding* the platform session (its token carries the identity's platform roles and no
 * company), so entering the platform shell there would mint an equivalent second session, rotate the CM-Secret
 * and reload the page to arrive where it already is.
 *
 * The chrome switcher deliberately uses {@link canEnter} instead, because by the time it renders the session
 * has entered a tenant — which narrowed `PlatformOwner` out of it — and the platform shell is genuinely
 * somewhere else. Both post-sign-in surfaces ({@link SigningInComponent}'s auto-select and
 * {@link ChooseContextComponent}) share this predicate so the two cannot drift apart.
 */
export function needsEntryAfterSignIn(context: SelectableContext): boolean {
  return canEnter(context) && context.kind !== SelectableContextKind.Platform;
}

/**
 * A stable identity for `@for (… ; track …)` and for comparing a context against the active session.
 *
 * `employeeId` is part of it for the same reason it is part of the backend's match: two employees of one
 * company produce two entries whose every other field is identical, and a `track` expression that cannot tell
 * them apart makes Angular reuse one DOM node for both.
 */
export function contextKey(context: SelectableContext): string {
  return `${context.kind}:${context.companyId ?? '-'}:${context.shardId ?? '-'}:${context.view}`
    + `:${context.employeeId ?? '-'}`;
}

/** German label for the application a context opens into. */
export function viewLabel(view: ContextView): string {
  switch (view) {
    case ContextView.Platform:
      return 'Plattformverwaltung';
    case ContextView.Management:
      return 'Verwaltung';
    case ContextView.Employee:
      return 'Mitarbeiterbereich';
    case ContextView.Operator:
      return 'Produktionsansicht';
    default:
      // A newer backend can add a view this build has no wording for. Naming it "unknown" is more honest than
      // falling back to "Verwaltung", which would claim the wrong application.
      return 'Unbekannter Bereich';
  }
}

/** The headline for a context row: the tenant it belongs to. */
export function contextTitle(context: SelectableContext): string {
  if (context.kind === SelectableContextKind.Platform) return 'Plattform';
  // A company whose name failed to resolve still has to render as something identifiable rather than blank.
  return context.companyName || context.companyId || 'Unbenannte Firma';
}

/** The second line: which part of the tenant, spelled out rather than left implicit. */
export function contextScope(context: SelectableContext): string {
  switch (context.kind) {
    case SelectableContextKind.Platform:
      return 'Alle Firmen und Standorte';
    case SelectableContextKind.Company:
      return 'Gesamte Firma';
    case SelectableContextKind.Employee:
      // WHO, not where. An employee context has no shard to name, and the person is the thing that actually
      // distinguishes it — two employee entries of one company differ in nothing else on the card.
      return context.employeeName
        ? `Persönlicher Zugang · ${context.employeeName}`
        : 'Persönlicher Zugang';
    default:
      return context.shardName || context.shardId || 'Unbenannter Standort';
  }
}

/**
 * Short German label for a central role name, for the pills that say what a context confers.
 *
 * Deliberately SHORTER than `InviteLandingComponent.roleLabel`, which sells a role to somebody who has never
 * held it ("Firmeninhaber:in — alle Standorte und Einstellungen"). Here the role is one chip among several on a
 * row that already names the tenant, so the descriptive tail would wrap the row and say nothing new.
 */
export function roleLabel(role: string): string {
  switch (role) {
    case 'PlatformOwner':
      return 'Plattformverwaltung';
    case 'CompanyOwner':
      return 'Firmeninhaber:in';
    case 'ShardAdmin':
      return 'Standortverwaltung';
    case 'ShiftLead':
      return 'Schichtleitung';
    case 'Payroll':
      return 'Lohnbuchhaltung';
    case 'Steuerberater':
      return 'Steuerberatung';
    case 'Employee':
      return 'Mitarbeiter:in';
    case 'Operator':
      return 'Betriebsleitung';
    case 'Auditor':
      return 'Protokolleinsicht';
    case 'SensitiveDataAccessor':
      return 'Sensible Daten';
    default:
      // A role this build has no wording for still has to render as something. The raw name is more useful
      // than a placeholder — it is at least searchable.
      return role;
  }
}

/**
 * What a central role actually confers, in full sentences — the text behind the role tooltips on the access
 * screen and the invite landing.
 *
 * **This is a security affordance, not decoration.** An administrator reads it to decide what to hand a person,
 * so every sentence is derived from the backend gate that enforces it rather than from the role's name:
 *
 * | Role | Derived from |
 * | --- | --- |
 * | `CompanyOwner` | `GrantIssuancePolicy.CanIssue` (may issue anywhere inside its own company); the management `[Authorize]` lists on `AreaController`/`TableController`/`FloorPlanController`/`RoleGrantController`; `AuditController` (company-wide audit read); **absent** from `SubmissionController.Reveal`, which is why unmasked data still needs the additive grant |
 * | `ShardAdmin` | `GrantIssuancePolicy.CanIssue` (shard scope only — a company-scoped invitation is refused); the same management lists; **absent** from `AuditController` and `SubmissionController.Reveal` |
 * | `Operator` | `Role.Operator` (standalone, read-only); the *List/Get* actions of `AreaController`/`TableController`/`FloorPlanController` and `DeviceConfigurationController.GetEffectiveForShard`; `ContextViewMap` gives it its own `ContextView.Operator` shell |
 * | `ShiftLead` | `ShiftMakerController.Download` + `ShiftMakerHub.PushLiveUpdate`/`PushLiveIndexUpdates` (the only gates that name it) narrowed by `ShiftLeadGuard`/`ShiftLeadAccess` — exactly one currently-running event, its own hours widened by the configured grace margins; **deliberately absent** from the hub's unrestricted `PushGeneralUpdate`/`PushGeneralIndexUpdates`, which would be the same plan without the window |
 * | `Auditor` | `AuditController` + `AuditResolveController` (company-scoped read); additive per `ContextViewMap.For` returning `null`; separation of duties per `Role.Auditor` |
 * | `SensitiveDataAccessor` | `SubmissionController.Reveal` / `DownloadFile` / `Download?unmasked=true`, each of which also writes an `AuditRecord`; additive per `ContextViewMap.For` |
 * | `Payroll` | the `[DynamicAuthorize(..., Roles.Payroll)]` **read** endpoints on `EmployeeController` (get/list/shift overview/worktime stats), `Company`+`Document` `EmploymentRelationshipController` (the Zeiten export) and the Forms `DefinitionController`/`SubmissionController` |
 * | `Steuerberater` | `Role.Steuerberater` (company-scoped, external, cascading into every location per `RoleGrantExpander.CascadeToEveryShard`) and the fiscal surface Feature #241 gates on it — the TSS reads, the DSFinV-K data export and correcting a filed closing. **Landed: `TSSController.GetCurrent`/`History` (#769, `[DynamicAuthorize("Legal", MetaRole.Admin, Roles.Steuerberater)]`); the DSFinV-K export at `/legal/exports` (#770) and correcting a filed closing at `/legal/closing-corrections` (#771), both gated `[Authorize(Roles = PlatformOwner,CompanyOwner,ShardAdmin,Steuerberater)]` on `/fiscal/exports/*` and `/fiscal/closing-corrections/*`.** Deliberately absent from the other `/legal/*` reads (provider settings and credentials), and absent from everything staff-, article-, floor-plan- and settings-related, and from `SubmissionController.Reveal` |
 *
 * An unknown role answers with the empty string on purpose: a build that has never heard of a role knows
 * nothing about what it confers, and inventing a plausible sentence for it would be worse than saying nothing.
 * Callers therefore have to tolerate `''` (an empty `matTooltip` never opens).
 */
export function roleDescription(role: string): string {
  switch (role) {
    case 'PlatformOwner':
      return 'Plattformweite Verwaltung: jede Firma, jeder Standort und die Plattformeinstellungen. '
        + 'Wird ausschließlich über die Sicherheitsgruppe beim Anmeldeanbieter vergeben und lässt sich hier '
        + 'bewusst nicht einladen.';
    case 'CompanyOwner':
      return 'Vollzugriff auf die gesamte Firma und alle ihre Standorte: Stammdaten, Personal, Artikel, Geräte '
        + 'und Einstellungen. Darf selbst Zugänge einladen und entziehen — firmenweit wie für einzelne '
        + 'Standorte — und das Prüfprotokoll der eigenen Firma lesen. Sensible Formulardaten bleiben trotzdem '
        + 'maskiert; dafür ist zusätzlich „Sensible Daten“ nötig.';
    case 'ShardAdmin':
      return 'Verwaltet genau einen Standort: dessen Personal, Artikel, Tischplan, Geräte und Einstellungen. '
        + 'Darf dort auch selbst einladen, aber ausschließlich für denselben Standort — eine firmenweite '
        + 'Einladung wird abgelehnt. Kein Zugriff auf das Prüfprotokoll und keine unmaskierten sensiblen Daten.';
    case 'Operator':
      return 'Nur-Lese-Blick auf den laufenden Betrieb eines Standorts: offene Tische und Tischplan. Öffnet '
        + 'dafür eine eigene, schlanke Ansicht statt der Verwaltung. Ändern lässt sich nichts, und Personal, '
        + 'Artikel, Auswertungen und Einstellungen bleiben verschlossen.';
    case 'ShiftLead':
      return 'Nur der Schichtplan der Veranstaltung, die gerade läuft: ansehen und währenddessen ändern. Das '
        + 'Zeitfenster ist die Veranstaltung selbst plus dem eingestellten Vor- und Nachlauf — davor und danach '
        + 'ist nichts erreichbar, weder kommende Veranstaltungen noch die Historie. Firmenweit vergeben gilt '
        + 'dasselbe enge Zeitfenster an jedem Standort.';
    case 'Payroll':
      return 'Für eine externe Lohnbuchhaltung: firmenweiter Lesezugriff auf Mitarbeitende, '
        + 'Beschäftigungsverhältnisse, Arbeitszeiten samt Zeiten-Export und die Formularübersicht. Ändern darf '
        + 'sie nichts; Kasse, Artikel und Einstellungen bleiben verschlossen. Unmaskierte Daten nur zusammen '
        + 'mit „Sensible Daten“.';
    case 'Steuerberater':
      return 'Für eine externe Steuerberatung: firmenweiter Zugriff auf die steuerlich relevanten '
        + 'Kassenunterlagen jedes Standorts — die technische Sicherheitseinrichtung samt ihrer Vorgänger, die '
        + 'Kassenabschlüsse und die Datenexporte für eine Betriebsprüfung, dazu das erneute Übermitteln '
        + 'bereits übermittelter Abschlüsse. Personal, Artikel, Tischplan, Auswertungen und Einstellungen bleiben '
        + 'verschlossen. Unmaskierte Formulardaten nur zusammen mit „Sensible Daten“.';
    case 'Auditor':
      return 'Zusätzliche Berechtigung: liest das Prüfprotokoll der Firma — wer wann was getan hat. Sie ersetzt '
        + 'keine Rolle, sondern kommt zu einer anderen hinzu, und öffnet bewusst keinen Zugriff auf sensible '
        + 'Daten (Aufgabentrennung: wer sensible Daten liest, soll nicht das Protokoll darüber kontrollieren).';
    case 'SensitiveDataAccessor':
      return 'Zusätzliche Berechtigung: liest Formulardaten unmaskiert statt verdeckt — etwa Bank- und '
        + 'Ausweisdaten samt Anhängen. Sie ersetzt keine Rolle, sondern kommt zu einer anderen hinzu; ohne sie '
        + 'sehen auch Firmeninhaber:innen nur maskierte Werte. Jeder unmaskierte Abruf wird protokolliert.';
    case 'Employee':
      return 'Mitarbeiterbereich für Beschäftigte der Firma. Wird nicht eingeladen, sondern entsteht mit dem '
        + 'Beschäftigungsverhältnis unter „Mitarbeiter“.';
    default:
      // No sentence at all for a role this build does not know. See the note above: an empty tooltip is honest,
      // a guessed one is a misleading security affordance.
      return '';
  }
}

/**
 * The part of the active session that says **which context it is in**.
 *
 * Structurally typed rather than importing `UserJwt`: the session object is what the backend put in
 * localStorage, this file is otherwise free of app-model imports, and the shape below is the whole of what the
 * comparison needs. `UserJwt` satisfies it.
 */
export interface ActiveContext {
  company?: {id?: string} | null;
  shard?: {id?: string} | null;
  /**
   * The view this session was entered into (`ctx_view` / `JWTUser.ContextView` / `JWTEmployee.ContextView`).
   * Absent on a session that never entered a context — a password login, or one stored before this build.
   */
  contextView?: ContextView | null;
  /**
   * The employee this session was minted as (`ctx_emp`), absent on every non-employee session. NOT the same as
   * the subject: a grant-derived context can carry `ContextView.Employee` with an identity as its subject, so
   * "the id, when the view is Employee" would name the wrong thing for exactly that session.
   */
  contextEmployeeId?: string | null;
}

/**
 * Whether a context is the one the active session is already in.
 *
 * **Compared on the entry's full identity — `(company, shard, view, employee)`** — because that is what
 * identifies a context: it is the tuple `SelectableContext` carries, the tuple `POST /auth/context/enter` matches
 * on, and the tuple {@link contextKey} is built from. Anything coarser marks more than one card.
 *
 * This used to compare company + shard only, on the reasoning that "a session's token carries roles, not the view
 * it was minted for, so which of my two views am I in is not knowable — marking the tenant is a fact, marking a
 * view would be a guess." The premise was true and is no longer: the session now **records** the context it was
 * entered into (see {@link ActiveContext}), so the view is a fact too. What the coarse comparison cost in the
 * meantime was not cosmetic — the caller marks the current card and *disables* it, so two contexts of one company
 * were both marked and neither was reachable from the other.
 *
 * The employee is compared as well as the view, because the view alone still is not enough: one account can
 * bracket two employees of the same company, and both entries are `(company, no shard, Employee)`.
 *
 * **An unstamped session falls back to the old company + shard comparison, deliberately.** A session minted
 * before this build has no view to compare, and the two possible errors are not symmetrical: marking too much
 * disables a card that was reachable, while marking too little offers a re-entry into the session you are already
 * holding — which rotates the secret, re-mints, and reloads the page onto itself. So the fallback is the one that
 * over-marks.
 */
export function isCurrentContext(context: SelectableContext, active: ActiveContext | null | undefined): boolean {
  if (!active) return false;

  const companyId = active.company?.id;
  const shardId = active.shard?.id;

  // No recorded view: the session predates the stamp (or never entered a context). Coarse, over-marking rule.
  if (active.contextView === undefined || active.contextView === null)
    return isCurrentTenant(context, companyId, shardId);

  if (context.view !== active.contextView) return false;
  if (context.kind === SelectableContextKind.Platform) return !companyId;
  if (!companyId || context.companyId !== companyId) return false;
  // A company-wide context matches a session that carries no shard; a shard context needs the shard to match.
  if ((context.shardId ?? '') !== (shardId ?? '')) return false;

  // Same tenant, same view — the only thing left that can differ is WHO. Empty on both sides for every context
  // that is not a bracketed employee, so it costs those nothing.
  return (context.employeeId ?? '') === (active.contextEmployeeId ?? '');
}

/**
 * The pre-{@link isCurrentContext} rule: same tenant, whatever the view.
 *
 * Kept as the fallback for a session that does not record its view, and **only** for that. It is not a weaker
 * version of the real comparison — it answers a different question ("is this my tenant?") and is used here
 * because over-marking is the safe direction when the real question cannot be answered.
 */
function isCurrentTenant(context: SelectableContext, companyId?: string, shardId?: string): boolean {
  if (context.kind === SelectableContextKind.Platform) return !companyId;
  if (!companyId || context.companyId !== companyId) return false;
  return (context.shardId ?? '') === (shardId ?? '');
}
